import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { GrowthTag } from '@/components/GrowthTag';
import { formatBRL, formatBRLShort, formatNumber, formatPercent } from '@/lib/format';
import type { Metrics, PeriodKey } from '@/lib/types';

/** As cores fixas da página: o vendido é sempre a série 1, o depósito a série 3. */
export const METRIC_TOKENS = { sold: '--series-1', deposit: '--series-3' } as const;

const plural = (count: number, one: string, many: string) => `${formatNumber(count)} ${count === 1 ? one : many}`;
const shareOf = (part: number, whole: number) => (whole > 0 ? part / whole : 0);

interface MetricsLedgerProps {
  data: Metrics;
  /** O período como se lê, ex.: "Este mês" ou "01/03/2026 a 31/03/2026". */
  when: string;
  /** "por Ana" quando o administrador filtrou um membro. */
  who: string;
  onPeriod: (period: PeriodKey) => void;
}

/**
 * O topo da página: dois livros lado a lado. À esquerda, o que se vendeu no
 * período — um fluxo, que se compara com o período anterior. À direita, o que
 * os clientes têm na corretora — uma posição, a de hoje, que o filtro de
 * período não muda.
 */
export function MetricsLedger({ data, when, who, onPeriod }: MetricsLedgerProps) {
  const { overall, deposits } = data;
  const comparable = data.period.key !== 'all';
  const hasSales = overall.receiptCount > 0;

  return (
    <section className="card ledger" aria-label="Resumo do período">
      <div className="ledger-side">
        <LedgerHead token={METRIC_TOKENS.sold} title={`Vendido${who}`} context={when} />
        <div className="ledger-value">
          <span className="ledger-number">{formatBRL(overall.soldTotal)}</span>
          {comparable && hasSales ? <GrowthTag growth={overall.growth} previousLabel={formatBRL(overall.previousSoldTotal)} /> : null}
        </div>

        {hasSales ? (
          <dl className="ledger-figures">
            <LedgerFigure label="Comprovantes" value={formatNumber(overall.receiptCount)} />
            <LedgerFigure label="Ticket médio" value={formatBRLShort(overall.averageTicket)} />
            <LedgerFigure
              label="Repasses pendentes"
              value={formatNumber(overall.pendingTransfers)}
              sub={`${formatBRLShort(overall.transferTotal)} puxados`}
              tone={overall.pendingTransfers > 0 ? 'warning' : undefined}
            />
          </dl>
        ) : (
          <EmptySales periodKey={data.period.key} when={when} onPeriod={onPeriod} />
        )}
      </div>

      <div className="ledger-side">
        <LedgerHead token={METRIC_TOKENS.deposit} title="Na corretora" context="posição de hoje" />
        <div className="ledger-value">
          <span className="ledger-number">{formatBRL(deposits.total)}</span>
        </div>

        {deposits.leadCount > 0 ? (
          <>
            <dl className="ledger-figures">
              <LedgerFigure label="Clientes com depósito" value={formatNumber(deposits.leadCount)} />
              <LedgerFigure label="Média por cliente" value={formatBRLShort(deposits.average)} />
              <LedgerFigure label="Maior depósito" value={formatBRLShort(deposits.largest)} />
            </dl>
            <Coverage deposits={deposits} when={when} />
          </>
        ) : (
          <p className="ledger-empty">
            Nenhum cliente com depósito informado. O valor fica no cadastro do cliente, em{' '}
            <Link to="/clientes">Clientes</Link> → <strong>Depositado na corretora</strong>.
          </p>
        )}
      </div>
    </section>
  );
}

function LedgerHead({ token, title, context }: { token: string; title: string; context: string }) {
  return (
    <div className="ledger-head">
      <span className="series-key" style={{ background: `var(${token})` }} aria-hidden />
      <h2 className="label ledger-title">{title}</h2>
      <span className="ledger-context">{context}</span>
    </div>
  );
}

function LedgerFigure({ label, value, sub, tone }: { label: string; value: string; sub?: ReactNode; tone?: 'warning' }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={tone}>{value}</dd>
      {sub ? <dd className="ledger-figure-sub">{sub}</dd> : null}
    </div>
  );
}

/** Quanto da carteira já tem depósito: a barra é a fração, o texto diz os números. */
function Coverage({ deposits, when }: { deposits: Metrics['deposits']; when: string }) {
  const share = shareOf(deposits.leadCount, deposits.portfolioCount);
  return (
    <div className="ledger-coverage">
      <div
        className="coverage-track"
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(share * 100)}
        aria-label="Clientes da carteira com depósito"
      >
        <span className="coverage-fill" style={{ width: `${Math.max(share * 100, 1.5)}%` }} />
      </div>
      <p className="ledger-coverage-text">
        <strong>{formatPercent(share)}</strong> da carteira tem depósito ({formatNumber(deposits.leadCount)} de{' '}
        {plural(deposits.portfolioCount, 'cliente', 'clientes')})
        {' · '}
        {deposits.updatedInPeriod > 0
          ? `${plural(deposits.updatedInPeriod, 'atualizado', 'atualizados')} em ${when.toLowerCase()}`
          : `nenhum atualizado em ${when.toLowerCase()}`}
      </p>
    </div>
  );
}

function EmptySales({ periodKey, when, onPeriod }: { periodKey: PeriodKey; when: string; onPeriod: (period: PeriodKey) => void }) {
  return (
    <div className="ledger-empty">
      <p>Nenhum comprovante em <strong>{when.toLowerCase()}</strong>.</p>
      <div className="ledger-empty-actions">
        {periodKey !== 'year' && periodKey !== 'all' ? (
          <button type="button" className="btn btn-sm" onClick={() => onPeriod('year')}>Ver o ano inteiro</button>
        ) : null}
        {periodKey !== 'all' ? (
          <button type="button" className="btn btn-sm" onClick={() => onPeriod('all')}>Ver todo o período</button>
        ) : (
          <Link to="/comprovantes" className="btn btn-sm btn-primary">Anexar um comprovante</Link>
        )}
      </div>
    </div>
  );
}
